import classnames from "classnames"
import { AnimatePresence, motion } from "framer-motion"
import PropTypes from "prop-types"
import React, { useCallback, useContext, useEffect, useState } from "react"
import styled from "styled-components"
import usePreviousEffect from "../hooks/usePreviousEffect"
import { media } from "../utils/styles"
import CardStackCard from "./CardStackCard"
import { cardSizeStyle } from "./ui"

const CARD_IN_S = 0.15
const CARD_OUT_S = 0.1

const cardMotion = {
  initial: { opacity: 0, scale: 0.625 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: CARD_IN_S, ease: "easeOut" }
  },
  exit: {
    opacity: 0,
    scale: 0.5,
    transition: { duration: CARD_OUT_S, ease: "easeOut" }
  }
}

const StyledContainer = styled.div`
  &.CardStack {
    position: relative;
    margin: 0 auto;
    background-color: transparent !important;
    transition: max-width 0.1s ease;
    ${cardSizeStyle}
  }

  &.CardStack .Card {
    /* Ensure CardStack is controlling the Card max-widths. */
    max-width: unset;
  }

  &.CardStack .CardStack__cards {
    display: flex;
  }

  /* motion wrappers are the flex items that stack. */
  &.CardStack:not(.CardStack--no-animate) .CardStack__card-wrap {
    width: 100%;
    transform-origin: 50% 50%;
  }

  &.CardStack:not(.CardStack--no-animate)
    .CardStack__card-wrap:not(:first-child) {
    margin-left: -100%;
  }

  &.CardStack:not(.CardStack--no-animate) .CardStack__card-wrap .Card {
    margin-left: 0;
  }

  ${media.max.phone`
    &.CardStack,
    &.CardStack .CardStack__cards,
    &.CardStack .CardStack__cards .Card,
    .CardStack__cards .CardStack__card-wrap {
      min-height: 100vh;
    }
  `}
`

export const CardStackContext = React.createContext()

export const useCardStackContext = () => useContext(CardStackContext)

const CardStack = ({
  card,
  cards,
  onCloseCard,
  onCloseCardStack,
  className,
  allowAnimate = true,
  ...passProps
}) => {
  const [cardStack, setCardStack] = useState(card ? [card] : cards ? cards : [])
  // Handles sizing the entire cardstack to a custom width.
  const [cardStackSize, setCardStackSize] = useState("medium")
  // is top card transitioning in?
  const [animating, setAnimating] = useState(false)
  // cards with unsaved changes (stored by card index)
  const [dirtyCards, setDirtyCards] = useState([])

  const handleAnimating = (animating) => {
    if (allowAnimate) setAnimating(animating)
  }

  const pushCard = useCallback(
    (card) => {
      if (animating) return
      if (card.component) {
        setCardStack([...cardStack, card])
      } else {
        // allows for swapping out props for same card component if card does not have component key
        setCardStack([
          ...cardStack.slice(0, -1),
          {
            ...cardStack[cardStack.length - 1],
            ...card
          }
        ])
      }
    },
    [cardStack, animating]
  )

  const popCard = useCallback(() => {
    if (animating) return
    setCardStack([...cardStack.slice(0, cardStack.length - 1)])
  }, [cardStack, animating])

  // update cardStack if new cards are passed
  // if card is passed, expect it never to update
  useEffect(() => {
    if (cards) setCardStack(cards)
  }, [cards])

  usePreviousEffect(
    ([prevCardStack]) => {
      if (cardStack.length > prevCardStack.length) handleAnimating(true)
      // if last card has been popped, call onCloseCardStack
      if (cardStack.length < prevCardStack.length && !cardStack.length) {
        onCloseCardStack && onCloseCardStack()
      }
    },
    [cardStack]
  )

  const handleCardDirty = useCallback(
    (isDirty, index) => {
      if (isDirty) {
        return setDirtyCards(dirtyCards.concat(index))
      } else if (dirtyCards.indexOf(index) > -1) {
        return setDirtyCards(dirtyCards.filter((i) => i !== index))
      }
    },
    [dirtyCards]
  )

  const handleCloseCard = useCallback(
    (card, index) => {
      // potentially prevent popping the card if onCloseCard returns false
      if (onCloseCard && onCloseCard(card, index) === false) return
      popCard()
      handleCardDirty(false, index)
    },
    [cardStack, popCard, onCloseCard]
  )

  const onCardTransitionComplete = useCallback((definition) => {
    if (definition === "animate") handleAnimating(false)
  }, [])

  const renderCard = (card, index) => (
    <CardStackCard
      {...passProps}
      index={index}
      card={card}
      isActive={index === cardStack.length - 1}
      isAnimating={animating}
      // pass pushCard as a way of allowing a card to push another
      // card on top of it without requiring context (allows for mixing route and inline cards)
      pushCard={pushCard}
      onClose={handleCloseCard}
      isDirty={dirtyCards.indexOf(index) > -1}
      prevIsDirty={dirtyCards.filter((i) => i < index).length > 0}
      onCardDirty={handleCardDirty}
    />
  )

  const cardStackUI = cardStack.length
    ? allowAnimate
      ? cardStack.map((card, index) => (
          <motion.div
            key={index}
            className="CardStack__card-wrap"
            variants={cardMotion}
            initial="initial"
            animate="animate"
            exit="exit"
            onAnimationComplete={onCardTransitionComplete}
          >
            {renderCard(card, index)}
          </motion.div>
        ))
      : cardStack.map((card, index) => (
          <React.Fragment key={index}>{renderCard(card, index)}</React.Fragment>
        ))
    : null

  return (
    <CardStackContext.Provider
      value={{
        pushCard: card ? pushCard : null,
        popCard: card ? popCard : null,
        forcePushCard: pushCard,
        forcePopCard: popCard,
        setCardStackSize
      }}
    >
      {!!cardStackUI && (
        <StyledContainer
          className={classnames("CardStack", className, {
            "CardStack--animating": animating,
            "CardStack--no-animate": !allowAnimate
          })}
          size={cardStackSize}
        >
          <div className="CardStack__cards">
            {allowAnimate ? (
              <AnimatePresence>{cardStackUI}</AnimatePresence>
            ) : (
              cardStackUI
            )}
          </div>
        </StyledContainer>
      )}
    </CardStackContext.Provider>
  )
}

CardStack.displayName = "CardStack"

const cardType = PropTypes.shape({
  component: PropTypes.func
})

CardStack.propTypes = {
  card: cardType,
  cards: PropTypes.arrayOf(cardType),
  allowAnimate: PropTypes.bool
}

export default CardStack
